
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-white font-['Montserrat']">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-[#e4f4ff]/20 via-white to-[#b3d9ff]/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center px-4 py-2 bg-[#e4f4ff]/30 rounded-full text-[#22577A] font-medium mb-6">
            <i className="ri-file-shield-line mr-2"></i>
            Legal Agreement
          </div>

          <h1 className="text-5xl lg:text-6xl font-bold text-[#22577A] mb-6">
            Terms of Service
          </h1>

          <div className="flex items-center gap-2 text-gray-600 mb-8">
            <i className="ri-calendar-line"></i>
            <p className="text-lg">
              <strong>Effective Date:</strong> January 1, 2025
            </p>
          </div>

          <div className="bg-gradient-to-br from-[#e4f4ff]/20 to-[#b3d9ff]/20 rounded-xl border-2 border-[#66b3ff]/30 p-6">
            <p className="text-gray-800 leading-relaxed">
              These MandaPost Terms of Service (these "Terms") constitute a binding agreement between you ("you", "your", or "Subscriber") and Osem Dynamics LLC ("us", "our", "we" or "MandaPost") governing your use of and access to our Services (as defined below).
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Table of Contents */}
          <div className="bg-gradient-to-br from-[#e4f4ff]/10 to-[#b3d9ff]/10 rounded-xl border border-gray-200 p-8 mb-12">
            <h2 className="text-2xl font-bold text-[#22577A] mb-6">Table of Contents</h2>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              {[
                '1. Definitions',
                '2. Your Plan',
                '3. Use of and Access to Services',
                '4. Third-Party Services',
                '5. Fees and Payment',
                '6. Cancellation and Termination',
                '7. Confidential Information',
                '8. Warranties and Disclaimer',
                '9. Indemnification',
                '10. Limitations of Liability',
                '11. Intellectual Property Rights',
                '12. Data Privacy',
                '13. General Provisions',
              ].map((item, idx) => (
                <a
                  key={idx}
                  href={`#section-${idx + 1}`}
                  className="text-[#22577A] hover:text-[#38a3a5] font-medium transition-colors"
                >
                  {item}
                </a>
              ))}
            </div>
          </div>

          {/* Important Notice */}
          <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-6 mb-12">
            <div className="flex items-start gap-4">
              <i className="ri-error-warning-line text-3xl text-yellow-600 flex-shrink-0"></i>
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Important Agreement</h3>
                <p className="text-gray-700 leading-relaxed">
                  If you are entering into these Terms on behalf of a company, organization, or other legal entity, you acknowledge that you are agreeing to these Terms on behalf of such entity and represent to MandaPost that you have the authority to bind such entity. By using or accessing the Services, you accept and agree to be bound by these Terms.
                </p>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="prose max-w-none space-y-12">

            {/* Section 1: Definitions */}
            <section id="section-1" className="scroll-mt-24">
              <div className="flex items-center gap-4 mb-6 pb-4 border-b-2 border-[#66b3ff]">
                <div className="w-12 h-12 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-xl">1</span>
                </div>
                <h2 className="text-3xl font-bold text-[#22577A]">Definitions</h2>
              </div>
              <div className="text-gray-700 leading-relaxed space-y-3 ml-16">
                <p><strong>"Account"</strong> means a unique account created for you to access the Subscription Services.</p>
                <p><strong>"Account Owner"</strong> means the individual or entity designated by Subscriber as the Account owner within the Application.</p>
                <p><strong>"Application(s)"</strong> means the web applications (and any mobile versions) through which the Subscription Services are delivered and accessed.</p>
                <p><strong>"Authorized User(s)"</strong> means an individual who is authorized by you to use the Services under your Account and on your behalf.</p>
                <p><strong>"Content"</strong> means all information, data, content, messages and other materials that you or your Authorized Users post or otherwise transmit via the Applications.</p>
                <p><strong>"Confidential Information"</strong> means non-public information disclosed by either party to the other party that a reasonable person would consider confidential.</p>
                <p><strong>"Plan"</strong> means the specific services and product features available to you based upon your subscription.</p>
                <p><strong>"Services"</strong> means, collectively, the Subscription Services provided to Subscriber under Subscriber's Plan.</p>
                <p><strong>"Subscription Services"</strong> means the proprietary social media marketing and automation subscription services provided by MandaPost.</p>
                <p><strong>"Subscription Term"</strong> means the subscription term set forth in the applicable Plan or Service Order.</p>
                <p><strong>"Third-Party Service"</strong> means any third-party product, service, or software provided under separate terms and conditions used in conjunction with the Services.</p>
              </div>
            </section>

            {/* Section 2: Your Plan */}
            <section id="section-2" className="scroll-mt-24">
              <div className="flex items-center gap-4 mb-6 pb-4 border-b-2 border-[#66b3ff]">
                <div className="w-12 h-12 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-xl">2</span>
                </div>
                <h2 className="text-3xl font-bold text-[#22577A]">Your Plan</h2>
              </div>
              <div className="text-gray-700 leading-relaxed space-y-4 ml-16">
                <p>
                  <strong>2.1 Plan Details.</strong> The Services available to you will be based on your Plan. If you purchased the Services within the Application, the details of your Plan will be set forth on the billing page within the Application.
                </p>
                <p>
                  <strong>2.2 Add-Ons.</strong> Any additional Services or Service features, including additional users, profiles, or other features that you later add to an existing Plan during your Subscription Term shall be subject to these Terms.
                </p>
              </div>
            </section>

            {/* Section 3: Use of and Access to Services */}
            <section id="section-3" className="scroll-mt-24">
              <div className="flex items-center gap-4 mb-6 pb-4 border-b-2 border-[#66b3ff]">
                <div className="w-12 h-12 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-xl">3</span>
                </div>
                <h2 className="text-3xl font-bold text-[#22577A]">Use of and Access to Services</h2>
              </div>
              <div className="text-gray-700 leading-relaxed space-y-4 ml-16">
                <p>
                  <strong>3.1 Right to Use the Services.</strong> Subject to your compliance with these Terms, MandaPost grants to you a limited, non-exclusive, non-transferable, non-sublicensable revocable right during the Subscription Term to use the Services solely for your own internal business operations.
                </p>
                <p>
                  <strong>3.2 Protection Against Unauthorized Use.</strong> You agree to use reasonable efforts to prevent any unauthorized use of the Services and to immediately notify us if you discover any unauthorized use.
                </p>
                <p>
                  <strong>3.3 Authorized Users.</strong> You acknowledge and agree that you are responsible for your Authorized Users' compliance with these Terms, all activity conducted under your Account, and for all Content that you or your Authorized Users post or transmit via the Services.
                </p>
                <p>
                  <strong>3.4 Use Restrictions.</strong> You must use the Services in accordance with these Terms and all applicable laws. We reserve the right to delete, edit, or remove content that we deem necessary to be removed.
                </p>
                <p>
                  <strong>3.5 Right to Suspend Services.</strong> We may restrict functionality or temporarily suspend your use of the Services if we reasonably believe such suspension is necessary to prevent unauthorized use or violations of these Terms.
                </p>
                <p>
                  <strong>3.6 Updates to Services.</strong> We reserve the right to modify features and functionalities of the Services from time to time in our sole discretion.
                </p>
              </div>
            </section>

            {/* Section 4: Third-Party Services */}
            <section id="section-4" className="scroll-mt-24">
              <div className="flex items-center gap-4 mb-6 pb-4 border-b-2 border-[#66b3ff]">
                <div className="w-12 h-12 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-xl">4</span>
                </div>
                <h2 className="text-3xl font-bold text-[#22577A]">Third-Party Services</h2>
              </div>
              <div className="text-gray-700 leading-relaxed space-y-4 ml-16">
                <p>
                  <strong>4.1 Connecting to Third-Party Services.</strong> The Applications allow you to connect to and use certain Third-Party Services such as social media platforms. You must comply with all terms and conditions of such Third-Party Services.
                </p>
                <p>
                  <strong>4.2 No Liability for Third-Party Services.</strong> MandaPost does not control, endorse, or assume liability for any Third-Party Service. We are not responsible for any changes to, or deletion of, your data by Third-Party Services.
                </p>
              </div>
            </section>

            {/* Section 5: Fees and Payment */}
            <section id="section-5" className="scroll-mt-24">
              <div className="flex items-center gap-4 mb-6 pb-4 border-b-2 border-[#66b3ff]">
                <div className="w-12 h-12 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-xl">5</span>
                </div>
                <h2 className="text-3xl font-bold text-[#22577A]">Fees and Payment</h2>
              </div>
              <div className="text-gray-700 leading-relaxed space-y-4 ml-16">
                <p>
                  <strong>5.1 Monthly Payment Plans.</strong> For Plans where you pay MandaPost on a monthly basis, we will charge you on the first day of your Subscription Term and automatically on the same date each subsequent month. Cancellations must be made by the current Account Owner through the Application.
                </p>
                <p>
                  <strong>5.2 Annual Payment Plans.</strong> For annual Plans, your Subscription Term will automatically renew for subsequent annual periods unless you cancel prior to the renewal date. MandaPost reserves the right to increase subscription fees on your renewal date.
                </p>
                <p>
                  <strong>5.3 Changes To Your Plan.</strong> If you upgrade your Plan or add any features during your Subscription Term, you will be charged the then-current price. No refunds or credits will be issued for downgrades or removed features.
                </p>
                <p>
                  <strong>5.4 Credit Card Authorization.</strong> By submitting your credit card information, you authorize our third-party payment processor to store such information and to charge the account until your Account is terminated.
                </p>
                <p>
                  <strong>5.5 Late Payments.</strong> Any amount not paid when due shall be subject to finance charges equal to one and one-half percent (1.5%) of the unpaid balance per month or the highest rate permitted by law, whichever is less.
                </p>
                <p>
                  <strong>5.6 Taxes.</strong> You are responsible for the payment of any applicable taxes resulting from your purchase or use of the Services.
                </p>
              </div>
            </section>

            {/* Section 6: Cancellation and Termination */}
            <section id="section-6" className="scroll-mt-24">
              <div className="flex items-center gap-4 mb-6 pb-4 border-b-2 border-[#66b3ff]">
                <div className="w-12 h-12 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-xl">6</span>
                </div>
                <h2 className="text-3xl font-bold text-[#22577A]">Cancellation and Termination</h2>
              </div>
              <div className="text-gray-700 leading-relaxed space-y-4 ml-16">
                <p>
                  <strong>6.1 Termination by You.</strong> You may cancel your Plan in accordance with the payment terms specified in Section 5, or if we fail to cure a material breach within thirty (30) days of written notice.
                </p>
                <p>
                  <strong>6.2 Termination by Us.</strong> We may terminate these Terms if you fail to comply with these Terms, do not pay your fees, or engage in conduct that could present substantial reputational harm to MandaPost.
                </p>
                <p>
                  <strong>6.3 Post Termination.</strong> Upon termination, you will pay any fees that have accrued, and we have the right to remove your Account information and Content within thirty (30) days.
                </p>
              </div>
            </section>

            {/* Section 7: Confidential Information */}
            <section id="section-7" className="scroll-mt-24">
              <div className="flex items-center gap-4 mb-6 pb-4 border-b-2 border-[#66b3ff]">
                <div className="w-12 h-12 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-xl">7</span>
                </div>
                <h2 className="text-3xl font-bold text-[#22577A]">Confidential Information</h2>
              </div>
              <div className="text-gray-700 leading-relaxed space-y-4 ml-16">
                <p>
                  <strong>7.1 Protection.</strong> The receiving party shall not disclose, use, transmit, or make available any Confidential Information of the disclosing party for any purpose outside the scope of these Terms.
                </p>
                <p>
                  <strong>7.2 Exclusions.</strong> Confidential Information shall not include information that was publicly known, rightfully known prior to receipt, obtained from a third party without breach, or independently developed.
                </p>
              </div>
            </section>

            {/* Section 8: Warranties and Disclaimer */}
            <section id="section-8" className="scroll-mt-24 bg-yellow-50 rounded-xl p-8 border-2 border-yellow-200">
              <div className="flex items-center gap-4 mb-6 pb-4 border-b-2 border-yellow-300">
                <div className="w-12 h-12 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-xl">8</span>
                </div>
                <h2 className="text-3xl font-bold text-[#22577A]">Warranties and Disclaimer</h2>
              </div>
              <div className="text-gray-700 leading-relaxed space-y-4 ml-16">
                <p>
                  <strong>8.1 Your Warranties.</strong> You represent and warrant that you will use the Services in compliance with all applicable laws and that all information you provide is accurate and rightfully yours to use.
                </p>
                <p className="uppercase font-semibold">
                  <strong>8.2 DISCLAIMER.</strong> THE SERVICES ARE PROVIDED ON AN "AS IS" BASIS, WITHOUT ANY WARRANTIES, GUARANTEES, CONDITIONS, OR REPRESENTATIONS OF ANY KIND. TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE EXPRESSLY DISCLAIM ANY AND ALL WARRANTIES, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
                </p>
              </div>
            </section>

            {/* Section 9: Indemnification */}
            <section id="section-9" className="scroll-mt-24">
              <div className="flex items-center gap-4 mb-6 pb-4 border-b-2 border-[#66b3ff]">
                <div className="w-12 h-12 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-xl">9</span>
                </div>
                <h2 className="text-3xl font-bold text-[#22577A]">Indemnification</h2>
              </div>
              <div className="text-gray-700 leading-relaxed space-y-4 ml-16">
                <p>
                  <strong>9.1 Indemnification by MandaPost.</strong> We agree to defend, indemnify, and hold you harmless from any third-party claims alleging that your use of the Services infringes or misappropriates the Intellectual Property Rights of such third party.
                </p>
                <p>
                  <strong>9.2 Indemnification by You.</strong> You agree to defend, indemnify, and hold harmless MandaPost from any third-party claims arising from your breach of these Terms, your use of Third-Party Services, or your Content.
                </p>
              </div>
            </section>

            {/* Section 10: Limitations of Liability */}
            <section id="section-10" className="scroll-mt-24 bg-red-50 rounded-xl p-8 border-2 border-red-200">
              <div className="flex items-center gap-4 mb-6 pb-4 border-b-2 border-red-300">
                <div className="w-12 h-12 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-xl">10</span>
                </div>
                <h2 className="text-3xl font-bold text-[#22577A]">Limitations of Liability</h2>
              </div>
              <div className="text-gray-700 leading-relaxed space-y-4 ml-16">
                <p className="uppercase font-semibold">
                  <strong>10.1 EXCLUSION OF DAMAGES.</strong> NEITHER PARTY WILL BE LIABLE TO THE OTHER FOR CONSEQUENTIAL, INCIDENTAL, INDIRECT, SPECIAL, EXEMPLARY, OR PUNITIVE DAMAGES ARISING OUT OF OR RELATED TO THESE TERMS, INCLUDING LOST PROFITS, REVENUE, BUSINESS, OR DATA.
                </p>
                <p className="uppercase font-semibold">
                  <strong>10.2 MONETARY CAP.</strong> THE MAXIMUM AGGREGATE LIABILITY OF MANDAPOST TO YOU SHALL NOT EXCEED THE TOTAL AMOUNT PAID BY YOU TO MANDAPOST DURING THE TWELVE MONTHS PRECEDING THE EVENT GIVING RISE TO THE CLAIM.
                </p>
              </div>
            </section>

            {/* Section 11: Intellectual Property Rights */}
            <section id="section-11" className="scroll-mt-24">
              <div className="flex items-center gap-4 mb-6 pb-4 border-b-2 border-[#66b3ff]">
                <div className="w-12 h-12 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-xl">11</span>
                </div>
                <h2 className="text-3xl font-bold text-[#22577A]">Intellectual Property Rights</h2>
              </div>
              <div className="text-gray-700 leading-relaxed space-y-4 ml-16">
                <p>
                  <strong>11.1 Your Content.</strong> You own all of the Content you provide to us. You grant to us a nonexclusive, revocable, worldwide, limited right to use your Content solely for the purpose of providing the Services to you.
                </p>
                <p>
                  <strong>11.2 Our Services.</strong> MandaPost and Osem Dynamics LLC own and retain all rights, title, and interest in and to the Services along with all Intellectual Property Rights related to the Services.
                </p>
              </div>
            </section>

            {/* Section 12: Data Privacy */}
            <section id="section-12" className="scroll-mt-24">
              <div className="flex items-center gap-4 mb-6 pb-4 border-b-2 border-[#66b3ff]">
                <div className="w-12 h-12 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-xl">12</span>
                </div>
                <h2 className="text-3xl font-bold text-[#22577A]">Data Privacy</h2>
              </div>
              <div className="text-gray-700 leading-relaxed space-y-4 ml-16">
                <p>
                  <strong>12.1 Permitted Disclosures.</strong> MandaPost may provide your data to third parties if required by law or as permitted by you to provide the Services. We take steps to ensure third-party service providers maintain commercially reasonable data practices.
                </p>
                <p>
                  <strong>12.2 Data Protection.</strong> If your use of our Services includes processing personal data subject to applicable data protection laws, additional data processing terms may apply.
                </p>
              </div>
            </section>

            {/* Section 13: General Provisions */}
            <section id="section-13" className="scroll-mt-24">
              <div className="flex items-center gap-4 mb-6 pb-4 border-b-2 border-[#66b3ff]">
                <div className="w-12 h-12 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-xl">13</span>
                </div>
                <h2 className="text-3xl font-bold text-[#22577A]">General Provisions</h2>
              </div>
              <div className="text-gray-700 leading-relaxed space-y-4 ml-16">
                <p>
                  <strong>13.1 Updates To Terms.</strong> We may revise and update these Terms from time to time. Continued use of the Services after we provide notice of updated Terms shall constitute your acceptance.
                </p>
                <p>
                  <strong>13.2 Governing Law.</strong> These Terms will be governed by and construed in accordance with the laws of the United States, without reference to choice of law rules.
                </p>
                <p>
                  <strong>13.3 Agreement to Arbitrate.</strong> You and MandaPost agree to resolve any claims through final and binding arbitration, except for claims related to intellectual property rights or confidentiality breaches.
                </p>
                <p>
                  <strong>13.4 Notices.</strong> Any notices under these Terms must be sent to <a href="mailto:legal@mandapost.com" className="text-[#22577A] hover:text-[#38a3a5] font-semibold">legal@mandapost.com</a>
                </p>
                <p>
                  <strong>13.5 Entire Agreement.</strong> These Terms are the final and complete expression of the agreement between the parties regarding the Services.
                </p>
                <p>
                  <strong>13.6 Severability.</strong> If any provision is held invalid, such provision shall be limited to the minimum extent such that the remaining provisions will continue in full force.
                </p>
              </div>
            </section>

          </div>

          {/* Contact CTA */}
          <div className="mt-16 bg-gradient-to-r from-[#22577A] to-[#38a3a5] rounded-2xl p-8 text-white">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <i className="ri-customer-service-line text-2xl"></i>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold mb-2">Questions About These Terms?</h3>
                <p className="text-white/90 mb-4">
                  If you have any questions about these Terms of Service, please contact our legal team.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <a
                    href="mailto:legal@mandapost.com"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-[#22577A] rounded-lg font-semibold hover:shadow-xl transition-all cursor-pointer"
                  >
                    <i className="ri-mail-line"></i>
                    legal@mandapost.com
                  </a>
                  <a
                    href="/contact"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-white text-white rounded-lg font-semibold hover:bg-white/10 transition-all cursor-pointer"
                  >
                    <i className="ri-message-3-line"></i>
                    Contact Support
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
